/**
 * PLAYER_BETTING (cee-lo) — sidebet phase. Everyone (including the
 * upcoming shooter) can place sidebets on the next roll. No banker
 * special case: all wallets open, all instructions identical.
 *
 * Available sidebets depend on whether the banker has already rolled.
 * Pre-banker-roll only the banker's-point sidebets are uncertain
 * (4+ / 3-); post-banker-roll the full set is open. Banner copy is
 * re-rendered in onUpdate because `update.bankerHasRolled` flips
 * mid-state when the banker's roll lands.
 *
 * Lock-in button is visible ONLY in betting states. Set true ONCE in
 * onEnter — calling it in onUpdate would kill the entry tween and
 * re-run the intro animation every game:update tick.
 *
 * Subtitle in the instruction banner collapses once both top hex
 * sidebet slots fill so the body copy doesn't compete with the bills
 * sitting overhead.
 */

import type { GameUpdate } from '@sdl/shared';
import { CeeloState } from '@sdl/shared';
import { State } from '../State.js';

export class PlayerBettingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.countdownOverlay?.stop();

    // Wallet open for everyone — no active-player special case here.
    await game.enterBettingMode({
      open: true,
      animated: update.animated,
      previousState: update.previousState,
    });

    // Lock-in button enters once, only in onEnter.
    game.bettingHUD3D?.setLockInVisible(true);
    // Lock in the sidebet selector options from the authoritative update
    // (pre-banker-roll → 4+/3-; post-roll → +bank wins/loses).
    game.primeSidebetOptions(update);

    // Fresh entry → fresh prompt. Subtitle collapses reactively in
    // onUpdate once both top hex slots fill.
    game.instructionBanner?.setSubtitleVisible(true);
    this.showInstruction(update);

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // bankerHasRolled flips mid-state when the banker's roll lands —
    // re-render banner copy AND re-prime the sidebet options (bank
    // wins/loses open up post-roll).
    this.game.primeSidebetOptions(update);
    this.showInstruction(update);

    // Collapse the subtitle once both top hex slots are taken.
    const topRowFilled = (this.game.bettingController?.countTopRowSidebets() ?? 0) >= 2;
    this.game.instructionBanner?.setSubtitleVisible(!topRowFilled);

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // Clear the BETTING activity pill — leaving the sidebet window.
    this._clearBettingActivity();

    // Preload the upcoming roller's ready dice + name label BEFORE the
    // rolling state's onEnter runs exitBettingMode. The dice + label
    // are staged in the scene at the table-center pickup pose, so by
    // the time the camera lerps from the betting anchor to the
    // shooting anchor they're already visible — no pop-in mid-lerp.
    //
    // Idempotent with the rolling state's own _showOpponentReadyDice
    // call (kept as a fallback for late-join, where state:full enters
    // the rolling state directly without an onExit firing here).
    if (update.gameState === CeeloState.BANKER_ROLL) {
      this._showOpponentReadyDice(update.bankerId, 3);
    } else if (update.gameState === CeeloState.PLAYER_ROLL) {
      this._showOpponentReadyDice(update.currentPlayerId, 3);
    }
  }

  private showInstruction(update: GameUpdate): void {
    const text = update.bankerHasRolled
      ? 'Place a sidebet: choose Bank Wins, Bank Loses, Point 4+ or Point 3-'
      : "Place a sidebet on the banker's point: Point 4+ or Point 3-";
    this.game.instructionBanner?.show({ title: 'Sidebetting', text }, update.animated);
  }
}
